import {getTestProps} from '@benchling/js-test-utils';
import {Button, Group, Modal, Stack, Text} from '@mantine/core';
import React, {useCallback, useMemo, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import buildListItemViewModelFromAgent from 'llm/chat-library/agents/buildListItemViewModelFromAgent';
import buildListItemViewModelFromExample from 'llm/chat-library/agents/buildListItemViewModelFromExample';
import ChatSettingsListingView from 'llm/chat-library/shared/list-detail/ChatSettingsListingView';
import type {ListGroup, ListItemViewModel} from 'llm/chat-library/shared/list-detail/listDetailTypes';
import {AgentsGroup} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {openChat} from 'llm/chat-library/sidepanel/ChatRouterUtil';
import RoutineDetailPanel, {DetailPanelMode} from 'llm/routines/RoutineDetailPanel';
import {ROUTINE_EXAMPLES, RoutineExample} from 'llm/routines/routineExamples';
import {
  archiveRoutine as archiveRoutineApi,
  pauseRoutine as pauseRoutineApi,
  resumeRoutine as resumeRoutineApi,
  runRoutine as runRoutineApi,
} from 'llm/routines/RoutinesDAO';
import {Routine, TriggerType} from 'llm/routines/RoutineTypes';
import useRoutines from 'llm/routines/useRoutines';
import notifications from 'notifications';
import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

/**
 * The Background agents tab's group tabs, in display order. `id` is the group tab value; `label` is
 * the tab text. The listing view derives the horizontal tab bar from these.
 *
 * Each group sources its rows differently (see `itemsByGroup` in the component): "All" lists the
 * tenant's real agents, while "Example agents" lists read-only, pre-configured templates that seed
 * the create form when clicked. Active vs. paused isn't a group — it's surfaced through each real
 * agent row's toggle.
 */
const AGENTS_GROUPS: Array<{id: AgentsGroup; label: string}> = [
  {id: AgentsGroup.ALL, label: 'All'},
  {id: AgentsGroup.EXAMPLE_AGENTS, label: 'Example agents'},
];

/**
 * The Background agents tab in the chat settings modal. It mirrors the Skills tab: a grouped
 * listing view backed by {@link useRoutines}, with a detail panel (the shared routine create/edit
 * form) shown when an agent is selected (via the URL's `selectedItemId`) or when creating a new one.
 *
 * "Agents" are modeled in code as "routines"; this reuses the routines domain layer (query, DAO,
 * detail form) while presenting them through the new modal's shared list/detail primitives.
 */
export default function ChatSettingsAgentContent({
  selectedItemId,
  group,
  onSelectItem,
  onGroupChange,
  onBack,
}: {
  selectedItemId: string | null;
  group: AgentsGroup;
  onSelectItem: (id: string) => void;
  onGroupChange: (group: AgentsGroup) => void;
  /** Returns to the listing (clears the selected agent). */
  onBack: () => void;
}): JSX.Element {
  const timeBasedEnabled = BenchlingConfig.LLM_ENABLE_ROUTINES_TIME_BASED;
  const eventBasedEnabled = BenchlingConfig.LLM_ENABLE_ROUTINES_EVENT_BASED;

  const {routines, isLoading, refetch} = useRoutines();
  const [isCreating, setIsCreating] = useState(false);
  // The example a user clicked in the "Example agents" group, opened as a prefilled create form. Null
  // for the plain "Add" (blank) create path.
  const [createExample, setCreateExample] = useState<RoutineExample | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<Routine | null>(null);

  // Only surface agents whose trigger type is enabled for this tenant.
  const enabledTriggerTypes = useMemo(() => {
    const types = new Set<TriggerType>();
    if (timeBasedEnabled) {
      types.add(TriggerType.TIME_BASED);
    }
    if (eventBasedEnabled) {
      types.add(TriggerType.EVENT_BASED);
    }
    return types;
  }, [timeBasedEnabled, eventBasedEnabled]);

  const visibleRoutines = useMemo(
    () => routines.filter((r) => enabledTriggerTypes.has(r.triggerType)),
    [routines, enabledTriggerTypes]
  );

  // Examples are gated by trigger type exactly like real agents, so a disabled trigger type never
  // seeds the create form with a config the tenant can't use.
  const visibleExamples = useMemo(
    () =>
      ROUTINE_EXAMPLES.filter((example) => {
        const {triggerType} = example.formState;
        return triggerType != null && enabledTriggerTypes.has(triggerType);
      }),
    [enabledTriggerTypes]
  );

  // Run the DAO mutation and refetch the listing.
  const runMutation = useCallback(
    async (mutation: () => Promise<unknown>) => {
      await mutation();
      await refetch();
    },
    [refetch]
  );

  const handleToggleActive = useCallback(
    async (routine: Routine, active: boolean) => {
      await runMutation(() => (active ? resumeRoutineApi(routine.id) : pauseRoutineApi(routine.id)));
    },
    [runMutation]
  );

  // Run a saved agent immediately and, on success, surface a toast linking to the new chat.
  const handleRun = useCallback(async (routine: Routine) => {
    const {chatSessionId} = await runRoutineApi(routine.id);
    notifications.success('Your Agent is running.', {
      actionText: 'View chat',
      onActionClick: () => {
        openChat(chatSessionId);
      },
    });
  }, []);

  const handleArchiveRequest = useCallback((routine: Routine) => {
    setArchiveTarget(routine);
  }, []);

  const closeArchiveModal = useCallback(() => {
    setArchiveTarget(null);
  }, []);

  const handleConfirmArchive = useCallback(async () => {
    const target = archiveTarget;
    setArchiveTarget(null);
    if (target == null) {
      return;
    }
    await runMutation(() => archiveRoutineApi(target.id, 'Retired'));
    // If the archived agent was the one open in the detail panel, return to the listing.
    if (selectedItemId === target.id) {
      onBack();
    }
  }, [archiveTarget, runMutation, selectedItemId, onBack]);

  const handleCloseDetail = useCallback(() => {
    setIsCreating(false);
    setCreateExample(null);
    onBack();
  }, [onBack]);

  // Rows in the "Example agents" group aren't real agents, so a click opens the create form prefilled
  // from the example rather than routing to the (non-existent) edit-by-id path used for real agents.
  const handleSelectItem = useCallback(
    (id: string) => {
      if (group === AgentsGroup.EXAMPLE_AGENTS) {
        // Resolve against the trigger-type-filtered examples (the ones actually rendered) so a
        // disabled example can never seed the create form.
        const example = visibleExamples.find((e) => e.id === id);
        if (example != null) {
          setCreateExample(example);
          setIsCreating(true);
        }
        return;
      }
      onSelectItem(id);
    },
    [group, onSelectItem, visibleExamples]
  );

  // Show the create/edit form when creating a new agent or when one is selected via the URL. The
  // detail panel fetches the selected agent by id itself, so it works even before the list loads.
  if (isCreating || selectedItemId != null) {
    const createMode: DetailPanelMode =
      createExample != null
        ? {type: 'create', example: createExample.formState, exampleId: createExample.id}
        : {type: 'create'};
    return (
      <RoutineDetailPanel
        key={isCreating ? `create-${createExample?.id ?? 'blank'}` : `edit-${selectedItemId}`}
        mode={isCreating ? createMode : {type: 'edit', routineId: selectedItemId ?? ''}}
        onClose={handleCloseDetail}
        onMutationSuccess={refetch}
        timeBasedEnabled={timeBasedEnabled}
        eventBasedEnabled={eventBasedEnabled}
      />
    );
  }

  const handlers = {
    onToggleActive: handleToggleActive,
    onRun: handleRun,
    onArchive: handleArchiveRequest,
  };

  // Each group sources its rows differently: real agents (with toggles/menus) vs. read-only example
  // templates. Keyed by group so adding a group is a compile-time-checked, one-line addition.
  const itemsByGroup: Record<AgentsGroup, Array<ListItemViewModel>> = {
    [AgentsGroup.ALL]: visibleRoutines.map((r) => buildListItemViewModelFromAgent(r, handlers)),
    [AgentsGroup.EXAMPLE_AGENTS]: visibleExamples.map((example) =>
      buildListItemViewModelFromExample(example)
    ),
  };

  const groups: Array<ListGroup> = AGENTS_GROUPS.map(({id, label}) => ({
    id,
    header: label,
    items: itemsByGroup[id],
  }));

  const addButton = (
    <Button
      variant="primary"
      size="xs"
      leftIcon={<BenchlingIcon icon="plus" />}
      onClick={() => {
        setCreateExample(null);
        setIsCreating(true);
      }}
      {...getTestProps({component: 'ChatSettingsAgentContent', element: 'addAgentButton'})}
    >
      Add
    </Button>
  );

  return (
    <>
      <ChatSettingsListingView
        header={{
          title: 'Agents',
          action: addButton,
        }}
        groups={groups}
        activeGroupId={group}
        onGroupChange={(value) => {
          onGroupChange(value as AgentsGroup);
        }}
        isLoading={isLoading}
        emptyMessage="No agents yet."
        selectedItemId={selectedItemId}
        onSelectItem={handleSelectItem}
      />
      <Modal
        opened={archiveTarget != null}
        onClose={closeArchiveModal}
        title="Archive Agent"
        centered={true}
        closeOnClickOutside={true}
        zIndex={ZINDEX_FLOATING_UI}
      >
        <Stack spacing="md">
          <Text size="sm">Are you sure? This will stop and remove this Agent.</Text>
          <Group position="right" spacing="xs">
            <Button variant="ghost" size="sm" onClick={closeArchiveModal}>
              Cancel
            </Button>
            <Button
              variant="warn"
              size="sm"
              onClick={handleConfirmArchive}
              {...getTestProps({component: 'ChatSettingsAgentContent', element: 'confirmArchiveButton'})}
            >
              Archive
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
